// src/pages/PublisherDashboard.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./PublisherDashboard.css";
import {
  FaHome,
  FaFileAlt,
  FaListAlt,
  FaUser,
  FaSignOutAlt,
  FaFolderPlus,
  FaFolderOpen,
} from "react-icons/fa";
import axios from "../api/axios";

import NewApplicationForm from "./NewApplicationForm";
import ApplicationsList from "./ApplicationsList";
import PublisherEditInline from "./PublisherEditInline";
import NewGroupForm from "./NewGroupForm";
import GroupsList from "./GroupsList";
import PublisherLetterResults from "./PublisherLetterResults";

export default function PublisherDashboard() {
  const username = localStorage.getItem("username") || "Баспа пайдаланушысы";

  const [activeTab, setActiveTab] = useState("home");
  const [profile, setProfile] = useState(null);

  const [editData, setEditData] = useState({
    email: "",
    short_name: "",
    address: "",
  });
  const [isEditing, setIsEditing] = useState(false);

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const [profileSuccess, setProfileSuccess] = useState("");
  const [locale, setLocale] = useState(localStorage.getItem("locale") || "kz");

  const [editingId, setEditingId] = useState(null);
  const [showPwdModal, setShowPwdModal] = useState(false);

  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState("");

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") {
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
      "Accept-Language": locale === "kz" ? "kk" : "ru",
    };
  }, [locale]);

  // ===== Bootstrap SSO directly inside dashboard =====
  useEffect(() => {
    let cancelled = false;

    const bootstrapSso = async () => {
      const params = new URLSearchParams(window.location.search);
      const ssoKey = params.get("sso_key");
      const existingToken = localStorage.getItem("token");

      const hasValidExistingToken =
        existingToken && existingToken !== "null" && existingToken !== "undefined";

      if (ssoKey) {
        try {
          const res = await axios.post("/publisher/auth/idp/exchange", { key: ssoKey });

          const token = res?.data?.token;
          const publisher = res?.data?.publisher;

          if (!token) {
            throw new Error("Token not returned");
          }

          localStorage.setItem("token", token);
          localStorage.setItem("username", publisher?.short_name || "Баспа");

          params.delete("sso_key");
          params.delete("sso");

          const cleanUrl =
            `${window.location.pathname}` +
            `${params.toString() ? `?${params.toString()}` : ""}` +
            `${window.location.hash}`;

          window.history.replaceState({}, document.title, cleanUrl);

          if (!cancelled) {
            setAuthReady(true);
            setAuthError("");
          }
          return;
        } catch (err) {
          localStorage.removeItem("token");
          localStorage.removeItem("username");

          const msg = err?.response?.data?.message || err?.message || "SSO exchange failed";

          if (!cancelled) {
            setAuthError(msg);
            setAuthReady(false);
          }

          window.location.href = `/publisher/login?sso_error=${encodeURIComponent(msg)}`;
          return;
        }
      }

      if (hasValidExistingToken) {
        if (!cancelled) {
          setAuthReady(true);
          setAuthError("");
        }
        return;
      }

      window.location.href = "/publisher/login";
    };

    bootstrapSso();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
  if (!authReady) return;

  axios
    .get('/publisher/me', { headers: getAuthHeaders() })
    .then((res) => {
      const pub = res?.data?.publisher ?? res?.data ?? null;
      setProfile(pub);
      setEditData({
        email: pub?.email || '',
        short_name: pub?.short_name || '',
        address: pub?.address || '',
      });
      if (pub?.short_name) {
        localStorage.setItem('username', pub.short_name);
      }
    })
    .catch((err) => {

    });
}, [authReady, locale]);

  // ===== Video instructions (Drive) =====
  const VIDEO_KZ =
    "https://drive.google.com/file/d/164JyxwGrORBZaPjVf1s8hqR-Z4Z6BERR/view?usp=sharing";
  const VIDEO_RU =
    "https://drive.google.com/file/d/1XsgQV6low6VhxozNch-bC4P_6N7agBEd/view?usp=sharing";

  const getDrivePreviewUrl = useCallback((viewUrl) => {
    const m = String(viewUrl || "").match(/\/file\/d\/([^/]+)/);
    const id = m?.[1];
    return id ? `https://drive.google.com/file/d/${id}/preview` : viewUrl;
  }, []);

  const instructionVideoUrl = locale === "kz" ? VIDEO_KZ : VIDEO_RU;
  const instructionVideoPreviewUrl = useMemo(
    () => getDrivePreviewUrl(instructionVideoUrl),
    [instructionVideoUrl, getDrivePreviewUrl]
  );

  // ===== Language =====
  const toggleLanguage = useCallback((next) => {
    const lang = next || (locale === "kz" ? "ru" : "kz");
    localStorage.setItem("locale", lang);
    setLocale(lang);
  }, [locale]);

useEffect(() => {
  const checkAuth = async (event) => {
    if (event?.persisted || !localStorage.getItem("token")) {
      try {
        await axios.get('/publisher/auth/me');
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("username");
        sessionStorage.clear();
        window.location.replace("/publisher/login");
      }
    }
  };

  window.addEventListener("pageshow", checkAuth);

  return () => {
    window.removeEventListener("pageshow", checkAuth);
  };
}, []);

  // ===== Handlers =====
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordSuccess("");

    try {
      await axios.put("/publisher/change-password", passwordData, {
        headers: getAuthHeaders(),
      });

      setPasswordSuccess(
        locale === "kz" ? "Құпиясөз сәтті өзгертілді" : "Пароль успешно изменен"
      );

      setPasswordData({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
    } catch (err) {
      setPasswordSuccess(
        locale === "kz"
          ? "Қате: құпиясөз өзгертілмеді"
          : "Ошибка: не удалось изменить пароль"
      );
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileSuccess("");

    try {
      const res = await axios.put("/publisher/me", editData, {
        headers: getAuthHeaders(),
      });

      const pub = res?.data?.publisher;
      setProfile(pub);

      setProfileSuccess(
        locale === "kz"
          ? "Профиль сәтті жаңартылды"
          : "Профиль успешно обновлен"
      );

      setIsEditing(false);
    } catch (err) {
      setProfileSuccess(
        locale === "kz"
          ? "Қате: профиль жаңартылмады"
          : "Ошибка: не удалось обновить профиль"
      );
    }
  };

const handleLogout = async () => {
  try {
    await axios.post(
      "/publisher/logout",
      {},
      {
        headers: getAuthHeaders(),
      }
    );
  } catch (err) {
    console.error("Logout error:", err);
  } finally {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    sessionStorage.clear();

    window.history.replaceState(null, "", "/publisher/login");
    window.location.replace("/publisher/login");
  }
};

  // ===== Content renderer =====
  const renderContent = () => {
    switch (activeTab) {
      case "home":
        return (
          <div className="pd-home">
            <header className="dashboard-header">
              <h1>
                {locale === "kz" ? "Қош келдіңіз, " : "Добро пожаловать, "}
                {profile?.short_name || username}!
              </h1>
            </header>
          </div>
        );

      case "instruction":
        return (
          <div className="pd-home">
            <header className="dashboard-header">
              <h1>{locale === "kz" ? "Нұсқаулық" : "Инструкция"}</h1>
            </header>

            <div className="pd-card pd-card--video">
              <div className="pd-video-head">
                <h3 className="pd-video-title">
                  {locale === "kz" ? "Видео нұсқаулық" : "Видео инструкция"}
                </h3>

                <a
                  className="pd-link"
                  href={instructionVideoUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {locale === "kz" ? "Google Drive-та ашу" : "Открыть в Google Drive"}
                </a>
              </div>

              <div className="pd-video-wrap">
                <iframe
                  title="instruction-video"
                  src={instructionVideoPreviewUrl}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        );

      case "summary":
        return <PublisherLetterResults locale={locale} />;

      case "new":
        return <NewApplicationForm />;

      case "group":
        return <NewGroupForm />;

      case "groupsList":
        return <GroupsList />;


      case "profile": {
        const expires = profile?.certificate_expires_at
          ? new Date(profile.certificate_expires_at)
          : null;

        const daysLeft = expires
          ? Math.ceil((expires - new Date()) / 86400000)
          : null;

        const pillClass =
          daysLeft == null
            ? "pill"
            : daysLeft <= 30
              ? "pill pill--danger"
              : daysLeft <= 90
                ? "pill pill--warn"
                : "pill pill--ok";

        return (
          <div className="pd-profile">
            <section className="pd-prof-left">
              <div className="pd-card pd-card--profile">
                <div className="pd-prof-head">
                  <div className="pd-prof-id">
                    <div className="pd-avatar">
                      {(profile?.short_name || username).slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="pd-prof-title">
                        {locale === "kz" ? "Профиль ақпараты" : "Информация о профиле"}
                      </h3>
                      <div className="pd-prof-sub">
                        {profile?.organization_name || "-"}
                      </div>
                    </div>
                  </div>

                  {!isEditing && (
                    <div className="pd-actions">
                      <button
                        className="pd-btn pd-btn--sm"
                        onClick={() => setIsEditing(true)}
                        type="button"
                      >
                        {locale === "kz" ? "Өңдеу" : "Изменить"}
                      </button>

                      <button
                        className="pd-btn pd-btn--ghost pd-btn--sm"
                        type="button"
                        onClick={() => setShowPwdModal(true)}
                      >
                        {locale === "kz" ? "Құпиясөзді өзгерту" : "Поменять пароль"}
                      </button>
                    </div>
                  )}
                </div>

                {profile ? (
                  isEditing ? (
                    <form onSubmit={handleProfileUpdate} className="pd-formgrid">
                      <div className="pd-formgroup">
                        <label>{locale === "kz" ? "Қысқа атауы" : "Краткое название"}</label>
                        <input
                          type="text"
                          value={editData.short_name}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              short_name: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="pd-formgroup">
                        <label>Email</label>
                        <input
                          type="email"
                          value={editData.email}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              email: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="pd-formgroup pd-formgroup--full">
                        <label>{locale === "kz" ? "Мекен-жай" : "Адрес"}</label>
                        <input
                          type="text"
                          value={editData.address}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              address: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="pd-row">
                        <button type="submit" className="pd-btn">
                          {locale === "kz" ? "Сақтау" : "Сохранить"}
                        </button>
                        <button
                          type="button"
                          className="pd-btn pd-btn--ghost"
                          onClick={() => setIsEditing(false)}
                        >
                          {locale === "kz" ? "Болдырмау" : "Отмена"}
                        </button>
                      </div>

                      {profileSuccess && <p className="pd-success">{profileSuccess}</p>}
                    </form>
                  ) : (
                    <div className="pd-kv">
                      <div className="pd-term">{locale === "kz" ? "Аты-жөні" : "ФИО"}</div>
                      <div className="pd-val">{profile.full_name || "-"}</div>

                      <div className="pd-term">Email</div>
                      <div className="pd-val">{profile.email || "-"}</div>

                      <div className="pd-term">{locale === "kz" ? "Ұйым" : "Организация"}</div>
                      <div className="pd-val">{profile.organization_name || "-"}</div>

                      <div className="pd-term">
                        {locale === "kz" ? "Қысқа атауы" : "Краткое название"}
                      </div>
                      <div className="pd-val">{profile.short_name || "-"}</div>

                      <div className="pd-term">БСН</div>
                      <div className="pd-val">{profile.bin || "-"}</div>

                      <div className="pd-term">{locale === "kz" ? "Сертификат" : "Сертификат"}</div>
                      <div className="pd-val pd-inline">
                        {expires ? expires.toLocaleDateString() : "-"}
                        {daysLeft != null && (
                          <span
                            className={`pd-pill ${pillClass.replace("pill", "pd-pill")}`}
                            style={{ marginLeft: 8 }}
                          >
                            {locale === "kz"
                              ? `${daysLeft} күн қалды`
                              : `осталось ${daysLeft} дн.`}
                          </span>
                        )}
                      </div>

                      <div className="pd-term">{locale === "kz" ? "Күйі" : "Статус"}</div>
                      <div className="pd-val">
                        <span className="pd-pill pd-pill--muted">{profile.status || "-"}</span>
                      </div>
                    </div>
                  )
                ) : (
                  <p className="pd-muted">
                    {locale === "kz" ? "Жүктелуде..." : "Загрузка..."}
                  </p>
                )}
              </div>
            </section>

            {showPwdModal && (
              <div
                className="pd-modal-backdrop"
                onClick={(e) => {
                  if (e.target.classList.contains("pd-modal-backdrop")) {
                    setShowPwdModal(false);
                  }
                }}
              >
                <div
                  className="pd-modal pd-modal--sm"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="pwd-title"
                >
                  <div className="pd-modal-head">
                    <h3 id="pwd-title">
                      {locale === "kz" ? "Құпиясөз өзгерту" : "Изменение пароля"}
                    </h3>
                    <button
                      type="button"
                      className="pd-close"
                      onClick={() => setShowPwdModal(false)}
                    >
                      ×
                    </button>
                  </div>

                  <form
                    onSubmit={handlePasswordChange}
                    className="pd-formgrid pd-formgrid--1c"
                  >
                    <div className="pd-formgroup">
                      <label>{locale === "kz" ? "Қазіргі құпиясөз" : "Текущий пароль"}</label>
                      <input
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            current_password: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div className="pd-formgroup">
                      <label>{locale === "kz" ? "Жаңа құпиясөз" : "Новый пароль"}</label>
                      <input
                        type="password"
                        value={passwordData.new_password}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            new_password: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div className="pd-formgroup">
                      <label>{locale === "kz" ? "Қайта енгізу" : "Повторите пароль"}</label>
                      <input
                        type="password"
                        value={passwordData.new_password_confirmation}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            new_password_confirmation: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div className="pd-row pd-row--tight">
                      <button type="submit" className="pd-btn pd-btn--sm">
                        {locale === "kz" ? "Өзгерту" : "Изменить"}
                      </button>
                      <button
                        type="button"
                        className="pd-btn pd-btn--ghost pd-btn--sm"
                        onClick={() => setShowPwdModal(false)}
                      >
                        {locale === "kz" ? "Болдырмау" : "Отмена"}
                      </button>
                    </div>

                    {passwordSuccess && <p className="pd-success">{passwordSuccess}</p>}
                  </form>
                </div>
              </div>
            )}
          </div>
        );
      }

      default:
        return (
          <header className="dashboard-header">
            <h1>
              {locale === "kz" ? "Қош келдіңіз, " : "Добро пожаловать, "}
              {profile?.short_name || username}!
            </h1>
          </header>
        );
    }
  };

  if (!authReady) {
    return (
      <div style={{ padding: 24, fontSize: 18 }}>
        {authError || (locale === "kz" ? "Кіру тексерілуде..." : "Проверка входа...")}
      </div>
    );
  }

  return (
    <div className="pubdash">
      <div className="dashboard-layout">
        <aside className="pd-sidebar" role="navigation" aria-label="Publisher sidebar">
          <h2 className="pd-title">EXPERTISE</h2>

          <ul className="pd-menu">
            <li
              onClick={() => setActiveTab("home")}
              className={`pd-item ${activeTab === "home" ? "is-active" : ""}`}
            >
              <FaHome style={{ marginRight: 8 }} />
              {locale === "kz" ? "Басты бет" : "Главная"}
            </li>

            <li
              onClick={() => setActiveTab("group")}
              className={`pd-item ${activeTab === "group" ? "is-active" : ""}`}
            >
              <FaFolderPlus style={{ marginRight: 8 }} />
              {locale === "kz" ? "Жаңа ОӘК" : "Новый УМК"}
            </li>

            <li
              onClick={() => setActiveTab("groupsList")}
              className={`pd-item ${activeTab === "groupsList" ? "is-active" : ""}`}
            >
              <FaFolderOpen style={{ marginRight: 8 }} />
              {locale === "kz" ? "Құрылған ОӘК" : "Созданные УМК"}
            </li>

            <li
              onClick={() => setActiveTab("summary")}
              className={`pd-item ${activeTab === "summary" ? "is-active" : ""}`}
            >
              <FaFileAlt style={{ marginRight: 8 }} />
              {locale === "kz" ? "Сараптамалық қорытынды" : "Экспертные решения"}
            </li>

            <li
              onClick={() => setActiveTab("profile")}
              className={`pd-item ${activeTab === "profile" ? "is-active" : ""}`}
            >
              <FaUser style={{ marginRight: 8 }} />
              {locale === "kz" ? "Профиль" : "Профиль"}
            </li>

            <li
              onClick={() => setActiveTab("instruction")}
              className={`pd-item ${activeTab === "instruction" ? "is-active" : ""}`}
            >
              <FaFileAlt style={{ marginRight: 8 }} />
              {locale === "kz" ? "Нұсқаулық" : "Инструкция"}
            </li>

            <li className="pd-item" onClick={handleLogout}>
              <FaSignOutAlt style={{ marginRight: 8 }} />
              {locale === "kz" ? "Шығу" : "Выход"}
            </li>
          </ul>

          <div className="pd-lang">
            <button
              className={`pd-lang-btn ${locale === "kz" ? "is-active" : ""}`}
              onClick={() => toggleLanguage("kz")}
              type="button"
            >
              KZ
            </button>
            <button
              className={`pd-lang-btn ${locale === "ru" ? "is-active" : ""}`}
              onClick={() => toggleLanguage("ru")}
              type="button"
            >
              RU
            </button>
          </div>
        </aside>

        <main className="dashboard-content">
          <section className="dashboard-body">{renderContent()}</section>
        </main>
      </div>
    </div>
  );
}